import { useState, useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "@/stores";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

interface CategoryTranslationUpdate {
  name_et: string | null;
}

const CategoriesTranslationsTable = observer(() => {
  const { documentCategoriesStore } = useStores();
  const [updates, setUpdates] = useState<Record<number, CategoryTranslationUpdate>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    documentCategoriesStore.loadAllCategories();
  }, [documentCategoriesStore]);

  useEffect(() => {
    const initial: Record<number, CategoryTranslationUpdate> = {};
    documentCategoriesStore.categoriesList.forEach((category) => {
      initial[category.id] = {
        name_et: category.name_et,
      };
    });
    setUpdates(initial);
  }, [documentCategoriesStore.categoriesList]);

  const handleSave = async (categoryId: number) => {
    const update = updates[categoryId];
    if (!update) return;

    setSavingId(categoryId);
    try {
      await documentCategoriesStore.updateCategory(categoryId, { name_et: update.name_et });
      toast.success("Category translation updated successfully");
    } catch (error) {
      toast.error("Failed to update category translation");
    } finally {
      setSavingId(null);
    }
  };

  const handleFieldChange = (categoryId: number, value: string | null) => {
    setUpdates((prev) => ({
      ...prev,
      [categoryId]: { name_et: value },
    }));
  };

  const parentNameMap = useMemo(() => {
    const map = new Map<number, string>();
    documentCategoriesStore.categoriesList.forEach((cat) => {
      map.set(cat.id, cat.category_name);
    });
    return map;
  }, [documentCategoriesStore.categoriesList]);

  const filteredCategories = useMemo(() => {
    const list = documentCategoriesStore.categoriesList;

    // Sort hierarchically: parents alphabetically, subcategories grouped under parent
    const sorted = [...list].sort((a, b) => {
      const parentA = a.parent_category_id ?? a.id;
      const parentB = b.parent_category_id ?? b.id;
      if (parentA !== parentB) {
        // Compare by parent name
        const nameA =
          (a.parent_category_id ? parentNameMap.get(a.parent_category_id) : a.category_name) ?? "";
        const nameB =
          (b.parent_category_id ? parentNameMap.get(b.parent_category_id) : b.category_name) ?? "";
        return nameA.localeCompare(nameB);
      }
      // Same parent group: parent first, then children alphabetically
      if (!a.parent_category_id && b.parent_category_id) return -1;
      if (a.parent_category_id && !b.parent_category_id) return 1;
      return a.category_name.localeCompare(b.category_name);
    });

    if (!searchQuery.trim()) {
      return sorted;
    }

    const query = searchQuery.toLowerCase();
    return sorted.filter((category) => {
      return (
        category.category_name.toLowerCase().includes(query) ||
        (category.name_et && category.name_et.toLowerCase().includes(query))
      );
    });
  }, [documentCategoriesStore.categoriesList, searchQuery, parentNameMap]);

  if (documentCategoriesStore.isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (documentCategoriesStore.error) {
    return (
      <div className="rounded-md bg-destructive/10 p-4 text-destructive">
        Error loading categories: {documentCategoriesStore.error}
      </div>
    );
  }

  if (filteredCategories.length === 0) {
    return (
      <>
        <div className="mb-4">
          <Input
            placeholder="Search categories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1"
          />
        </div>
        <div className="rounded-md border border-dashed p-8 text-center text-muted-foreground">
          {searchQuery.trim() ? "No categories match your search." : "No categories found."}
        </div>
      </>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Input
          placeholder="Search categories..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1"
        />
      </div>

      <div className="rounded-md border">
        <Table className="w-full" style={{ tableLayout: "fixed" }}>
          <TableHeader>
            <TableRow>
              <TableHead style={{ width: "35%" }}>Category Name (EN)</TableHead>
              <TableHead style={{ width: "45%" }}>Name (ET)</TableHead>
              <TableHead style={{ width: "20%" }} className="text-right">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCategories.map((category) => {
              const update = updates[category.id];
              if (!update) return null;

              const hasChanges = update.name_et !== category.name_et;

              return (
                <TableRow key={category.id}>
                  <TableCell style={{ width: "35%" }}>
                    <span className="text-sm font-medium">{category.category_name}</span>
                    {category.parent_category_id && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        ({parentNameMap.get(category.parent_category_id) ?? "..."})
                      </span>
                    )}
                  </TableCell>
                  <TableCell style={{ width: "45%" }}>
                    <Input
                      value={update.name_et || ""}
                      onChange={(e) => handleFieldChange(category.id, e.target.value || null)}
                      placeholder="Estonian name"
                      className="h-8 w-full"
                    />
                  </TableCell>
                  <TableCell style={{ width: "20%" }} className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSave(category.id)}
                      disabled={!hasChanges || savingId === category.id}
                    >
                      {savingId === category.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
});

export default CategoriesTranslationsTable;
